import { PROFILE_FONT } from "../../../constants/layout";

/** Match aumsampaan Reports page tokens */
export const REPORT_COLORS = {
  background: "#F1EBEB",
  primaryText: "#000000",
  tableHeader: "#000000",
  tableHeaderText: "#F1EBEB",
  button: "#1E3A5F",
  buttonHover: "#162C47",
  profit: "#16A34A",
  loss: "#DC2626",
  realized: "#B45309",
  realizedBg: "#FEF3C7",
  border: "#D6D3D1",
  white: "#FFFFFF",
  mutedText: "#494646",
};

export const REPORT_RADIUS = {
  sm: "8px",
  md: "12px",
  lg: "14px",
  pill: "999px",
};

export const REPORT_SHADOW = {
  card: "0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06)",
  button: "0 4px 14px rgba(30, 58, 95, 0.2)",
  buttonHover: "0 6px 20px rgba(22, 44, 71, 0.28)",
};

export const REPORT_BUILDER_GRID_SPACING = { xs: 1.25, sm: 1.5, md: 2, lg: 3 } as const;

export const REPORT_TYPO = {
  sectionHeading: {
    fontFamily: PROFILE_FONT,
    fontSize: { xs: 15, sm: 16, lg: 18 },
    fontWeight: 600,
    letterSpacing: -0.3,
    lineHeight: 1.3,
    color: REPORT_COLORS.primaryText,
  },
  pageSubtitle: {
    fontFamily: PROFILE_FONT,
    fontSize: { xs: 12, sm: 13, lg: 15 },
    color: REPORT_COLORS.mutedText,
    lineHeight: 1.4,
    mt: 0.5,
  },
  sectionTitle: {
    fontFamily: PROFILE_FONT,
    fontSize: { xs: 10, sm: 11, lg: 13 },
    fontWeight: 600,
    letterSpacing: { xs: 0.5, lg: 0.6 },
    textTransform: "uppercase" as const,
    color: REPORT_COLORS.mutedText,
    lineHeight: 1.4,
  },
  fieldLabel: {
    fontFamily: PROFILE_FONT,
    fontSize: { xs: 11, sm: 12, lg: 13 },
    fontWeight: 500,
    color: REPORT_COLORS.primaryText,
    letterSpacing: -0.02,
    mb: { xs: 0.5, lg: 1 },
  },
  body: {
    fontFamily: PROFILE_FONT,
    fontSize: { xs: 12, sm: 13, lg: 14 },
    fontWeight: 400,
    color: REPORT_COLORS.primaryText,
    lineHeight: { xs: 1.45, lg: 1.5 },
  },
};

export const REPORT_CARD_SX = {
  borderRadius: REPORT_RADIUS.lg,
  bgcolor: REPORT_COLORS.white,
  border: "none",
  boxShadow: REPORT_SHADOW.card,
  overflow: "visible",
};

export const REPORT_CARD_CONTENT_SX = {
  p: { xs: 1.5, sm: 2, md: 2.5, lg: 4 },
};

export const REPORT_FOCUS_RING = "0 0 0 2px rgba(30, 58, 95, 0.08)";

export const REPORT_BUILDER_SELECT_SX = {
  backgroundColor: REPORT_COLORS.white,
  fontFamily: PROFILE_FONT,
  color: REPORT_COLORS.primaryText,
  borderRadius: REPORT_RADIUS.sm,
  fontSize: { xs: 12, sm: 13, lg: 14 },
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "& .MuiSelect-select": {
    py: { xs: 0.85, sm: 1, lg: 1.1 },
    minHeight: { xs: "20px !important", lg: "24px !important" },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: REPORT_COLORS.border,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: REPORT_COLORS.button,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: REPORT_COLORS.button,
    borderWidth: 2,
  },
  "&.Mui-focused": {
    boxShadow: REPORT_FOCUS_RING,
  },
};

export const REPORT_INPUT_SX = {
  bgcolor: REPORT_COLORS.white,
  borderRadius: REPORT_RADIUS.sm,
  fontFamily: PROFILE_FONT,
  fontSize: { xs: 12, sm: 13, lg: 14 },
  "& .MuiOutlinedInput-root": {
    borderRadius: REPORT_RADIUS.sm,
    "& fieldset": { borderColor: REPORT_COLORS.border },
    "&:hover fieldset": { borderColor: REPORT_COLORS.button },
    "&.Mui-focused fieldset": {
      borderColor: REPORT_COLORS.button,
      borderWidth: 2,
    },
    "&.Mui-focused": { boxShadow: REPORT_FOCUS_RING },
  },
  "& .MuiInputBase-input": {
    fontFamily: PROFILE_FONT,
    fontSize: { xs: 12, sm: 13, lg: 14 },
  },
};

export const REPORT_INVESTOR_AUTOCOMPLETE_SX = {
  ...REPORT_INPUT_SX,
  "& .MuiOutlinedInput-root": {
    ...REPORT_INPUT_SX["& .MuiOutlinedInput-root"],
    flexWrap: "nowrap",
    overflow: "hidden",
    alignItems: "center",
    py: { xs: 0.35, sm: 0.5 },
    minHeight: { xs: 36, sm: 40 },
    maxHeight: { xs: 36, sm: 40 },
  },
  "& .MuiAutocomplete-tag": {
    m: 0,
    mr: 0.5,
    maxWidth: "calc(100% - 52px)",
    height: 24,
  },
  "& .MuiAutocomplete-input": {
    minWidth: 0,
    flexGrow: 1,
    py: "0 !important",
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

export const REPORT_CHECKBOX_SX = {
  color: REPORT_COLORS.border,
  p: 0.5,
  "&.Mui-checked": { color: REPORT_COLORS.button },
  "&.MuiCheckbox-indeterminate": { color: REPORT_COLORS.button },
};

export const REPORT_BUILDER_CHIP_SELECTED_SX = {
  backgroundColor: REPORT_COLORS.button,
  color: REPORT_COLORS.white,
  border: `1px solid ${REPORT_COLORS.button}`,
  fontFamily: PROFILE_FONT,
  fontSize: { xs: 11, sm: 12, lg: 13 },
  fontWeight: 600,
  height: { xs: 28, sm: 32, lg: 36 },
  borderRadius: REPORT_RADIUS.pill,
  boxShadow: REPORT_SHADOW.button,
  "&:hover": {
    backgroundColor: REPORT_COLORS.buttonHover,
    borderColor: REPORT_COLORS.buttonHover,
  },
};

export const REPORT_BUILDER_CHIP_UNSELECTED_SX = {
  backgroundColor: REPORT_COLORS.white,
  color: REPORT_COLORS.primaryText,
  border: `1px solid ${REPORT_COLORS.border}`,
  fontFamily: PROFILE_FONT,
  fontSize: { xs: 11, sm: 12, lg: 13 },
  fontWeight: 500,
  height: { xs: 28, sm: 32, lg: 36 },
  borderRadius: REPORT_RADIUS.pill,
  "&:hover": {
    borderColor: REPORT_COLORS.button,
    boxShadow: REPORT_SHADOW.card,
  },
};

export const REPORT_GENERATE_BUTTON_SX = {
  backgroundColor: REPORT_COLORS.button,
  color: REPORT_COLORS.white,
  textTransform: "none" as const,
  fontFamily: PROFILE_FONT,
  fontWeight: 600,
  fontSize: { xs: 13, sm: 14, lg: 16 },
  borderRadius: { xs: "10px", lg: "12px" },
  boxShadow: REPORT_SHADOW.button,
  border: `1px solid ${REPORT_COLORS.button}`,
  minWidth: { xs: "100%", sm: 160, lg: 200 },
  maxWidth: { xs: "100%", lg: 280 },
  px: { xs: 2, sm: 3, lg: 4 },
  height: { xs: 40, sm: 44, lg: 48 },
  "&:hover": {
    backgroundColor: REPORT_COLORS.buttonHover,
    borderColor: REPORT_COLORS.buttonHover,
    boxShadow: REPORT_SHADOW.buttonHover,
  },
  "&.Mui-disabled": {
    backgroundColor: "#D1D5DB",
    color: "#9CA3AF",
    borderColor: "#D1D5DB",
    boxShadow: "none",
  },
};

export const REPORT_QUICK_REPORTS_ROW_SX = {
  display: "flex",
  flexWrap: "nowrap",
  gap: { xs: 1, sm: 1.5 },
  overflowX: "auto",
  overflowY: "visible",
  pt: 1,
  pb: 1,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": { height: { xs: 4, sm: 6 } },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: REPORT_COLORS.border,
    borderRadius: 4,
  },
};

export const getQuickReportRowCardSx = (active: boolean) => ({
  flex: "0 0 auto",
  minWidth: 112,
  maxWidth: 140,
  p: { xs: 1.5, sm: 2 },
  borderRadius: REPORT_RADIUS.lg,
  bgcolor: REPORT_COLORS.white,
  cursor: "pointer",
  textAlign: "center" as const,
  boxSizing: "border-box" as const,
  border: active
    ? `2px solid ${REPORT_COLORS.button}`
    : `1px solid ${REPORT_COLORS.border}`,
  boxShadow: active
    ? "0 0 0 3px rgba(30, 58, 95, 0.14), 0 4px 14px rgba(30, 58, 95, 0.1)"
    : "none",
  transform: active ? "translateY(-1px)" : "none",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
  "&:hover": {
    borderColor: REPORT_COLORS.button,
    boxShadow: active
      ? "0 0 0 3px rgba(30, 58, 95, 0.14), 0 4px 14px rgba(30, 58, 95, 0.1)"
      : "0 0 0 1px rgba(30, 58, 95, 0.1)",
    transform: "translateY(-1px)",
  },
});

export const REPORT_MENU_PAPER_SX = {
  borderRadius: "12px",
  border: "1px solid #d1cccc",
  mt: 0.5,
  maxHeight: { xs: 280, md: 320 },
  boxShadow: REPORT_SHADOW.card,
  fontFamily: PROFILE_FONT,
  "& .MuiAutocomplete-option": {
    fontFamily: PROFILE_FONT,
    fontSize: { xs: 12, sm: 13 },
    py: 0.75,
  },
  "& .MuiMenuItem-root": {
    fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "1rem" },
    color: REPORT_COLORS.primaryText,
    py: { xs: 0.75, md: 1.1 },
    fontFamily: PROFILE_FONT,
  },
  "& .MuiMenuItem-root.Mui-selected": {
    backgroundColor: "rgba(30, 58, 95, 0.12)",
    color: REPORT_COLORS.button,
    fontWeight: 600,
  },
  "& .MuiMenuItem-root.Mui-selected:hover": {
    backgroundColor: "rgba(30, 58, 95, 0.12)",
  },
  "& .MuiMenuItem-root:hover": {
    backgroundColor: "rgba(30, 58, 95, 0.08)",
  },
  "&::-webkit-scrollbar": { width: 10 },
  "&::-webkit-scrollbar-track": { background: REPORT_COLORS.background },
  "&::-webkit-scrollbar-thumb": {
    background: "#C9C3C3",
    borderRadius: 8,
  },
};

export const REPORT_OUTPUT_BOTTOM_GAP = { xs: 1.5, md: 2 } as const;

export const REPORT_PAGE_SX = {
  bgcolor: REPORT_COLORS.background,
  color: REPORT_COLORS.primaryText,
  fontFamily: PROFILE_FONT,
  px: { xs: "12px", sm: "16px", md: "clamp(20px, 2.5vw, 40px)" },
  pt: { xs: "12px", sm: "14px", md: "clamp(16px, 2vw, 28px)" },
  pb: { xs: 2, md: 3 },
  boxSizing: "border-box" as const,
};

export const REPORT_PAGE_INNER_SX = {
  maxWidth: "min(100%, clamp(1120px, 92vw, 1720px))",
  mx: "auto",
  width: "100%",
  minWidth: 0,
};

export const REPORT_TABLE_OUTER_CARD_SX = {
  ...REPORT_CARD_SX,
  overflow: "hidden",
  boxShadow: `0 0 0 1px ${REPORT_COLORS.border}`,
};

export const REPORT_TABLE_CONTAINER_SX = {
  width: "100%",
  maxWidth: "100%",
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  bgcolor: REPORT_COLORS.white,
  maxHeight: { xs: "calc(100dvh - 160px)", md: 560 },
  "&::-webkit-scrollbar": { width: 8, height: 8 },
  "&::-webkit-scrollbar-track": { bgcolor: "rgba(0,0,0,0.04)" },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: REPORT_COLORS.border,
    borderRadius: 4,
  },
};

export const REPORT_TABLE_HEAD_SX = {
  backgroundColor: REPORT_COLORS.tableHeader,
  backgroundImage: "none",
  opacity: 1,
  borderBottom: "none",
  "& th": {
    backgroundColor: REPORT_COLORS.tableHeader,
    backgroundImage: "none",
    opacity: 1,
    fontWeight: 600,
    color: REPORT_COLORS.tableHeaderText,
    fontSize: { xs: "10px", sm: "11px", lg: "12px" },
    letterSpacing: "0.02em",
    textTransform: "none" as const,
    borderBottom: "none",
    py: { xs: 1, sm: 1.25, lg: 1.75 },
    px: { xs: 1, sm: 1.5, lg: 2.5 },
    fontFamily: PROFILE_FONT,
  },
};

export const REPORT_TABLE_HEAD_ROW_SX = {
  borderBottom: "none",
  backgroundColor: REPORT_COLORS.tableHeader,
  backgroundImage: "none",
  opacity: 1,
  "& th": {
    backgroundColor: REPORT_COLORS.tableHeader,
    backgroundImage: "none",
    opacity: 1,
    border: "none",
    color: REPORT_COLORS.tableHeaderText,
  },
};

export const REPORT_TABLE_ROW_SX = {
  backgroundColor: REPORT_COLORS.white,
  borderBottom: `1px solid ${REPORT_COLORS.border}`,
  "&:last-of-type": { borderBottom: "none" },
  "& td": {
    py: { xs: 1, sm: 1.25, lg: 1.75 },
    px: { xs: 1, sm: 1.5, lg: 2.5 },
    fontSize: { xs: "11px", sm: "12px", lg: "14px" },
    fontFamily: PROFILE_FONT,
  },
};

export const REPORT_OUTLINE_CONTROL_SX = {
  fontFamily: PROFILE_FONT,
  fontSize: { xs: 12, sm: 13, lg: 14 },
  fontWeight: 500,
  textTransform: "none" as const,
  color: REPORT_COLORS.primaryText,
  bgcolor: REPORT_COLORS.white,
  borderRadius: REPORT_RADIUS.md,
  border: `1px solid ${REPORT_COLORS.border}`,
  boxShadow: REPORT_SHADOW.card,
  px: { xs: 1.5, sm: 2 },
  py: { xs: 0.75, sm: 1 },
  "&:hover": {
    borderColor: REPORT_COLORS.button,
    bgcolor: REPORT_COLORS.white,
  },
};

export const REPORT_TABLE_TOOLBAR_SX = {
  flexShrink: 0,
  bgcolor: REPORT_COLORS.white,
  px: { xs: 2, md: 3 },
  py: 2,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 2,
  borderBottom: `1px solid ${REPORT_COLORS.border}`,
};

export const REPORT_FROZEN_TABLE_OUTER_CARD_SX = {
  ...REPORT_TABLE_OUTER_CARD_SX,
  display: "flex",
  flexDirection: "column",
  maxHeight: { xs: "calc(100dvh - 160px)", md: "calc(100dvh - 200px)" },
  overflow: "hidden",
};

export const REPORT_FROZEN_TABLE_CARD_HEADER_SX = {
  flexShrink: 0,
  bgcolor: REPORT_COLORS.white,
  px: { xs: 2.5, md: 4 },
  py: 3,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 2,
  borderBottom: `1px solid ${REPORT_COLORS.border}`,
};

export const REPORT_FROZEN_TABLE_CONTAINER_SX = {
  width: "100%",
  maxWidth: "100%",
  flex: "1 1 auto",
  minHeight: 0,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
  bgcolor: REPORT_COLORS.white,
  "&::-webkit-scrollbar": { width: 8, height: 8 },
  "&::-webkit-scrollbar-track": { bgcolor: "rgba(0,0,0,0.04)" },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: REPORT_COLORS.border,
    borderRadius: 4,
  },
};

export const REPORT_TABLE_ELEMENT_SX = {
  width: "100%",
  minWidth: "100%",
  tableLayout: "auto",
  borderCollapse: "separate",
  borderSpacing: 0,
};

export const REPORT_SUMMARY_CARD_SX = {
  ...REPORT_CARD_SX,
  p: 3,
  height: "100%",
};

export const REPORT_PRIMARY_BUTTON_SX = {
  backgroundColor: REPORT_COLORS.button,
  color: REPORT_COLORS.white,
  textTransform: "none" as const,
  fontFamily: PROFILE_FONT,
  fontWeight: 600,
  fontSize: { xs: 12, sm: 13, lg: 14 },
  borderRadius: REPORT_RADIUS.md,
  boxShadow: REPORT_SHADOW.button,
  border: `1px solid ${REPORT_COLORS.button}`,
  "&:hover": {
    backgroundColor: REPORT_COLORS.buttonHover,
    borderColor: REPORT_COLORS.buttonHover,
    boxShadow: REPORT_SHADOW.buttonHover,
  },
  "&.Mui-disabled": {
    backgroundColor: "#D1D5DB",
    color: "#9CA3AF",
    borderColor: "#D1D5DB",
    boxShadow: "none",
  },
};
